import { Box, Link, Typography, useTheme } from '@mui/material'
import { Scatter } from 'react-chartjs-2'
import { Chart as ChartJS, Plugin } from 'chart.js'
import { useTranslation } from 'react-i18next'
import { max, min } from 'lodash'
import { useAppSelector } from '../../store/hooks'
import { roundUpToNearest10 } from '../../shared/utils'
import { fsColors } from '../../shared/constants'

const ChartKPIA = ({
  value1,
  value2 = 0,
  value3 = 0,
  label,
  height = 400,
  isMean = true,
}: {
  value1: number
  value2?: number
  value3?: number
  label: string
  height?: number
  isMean?: boolean
}) => {
  const { currentLanguage } = useAppSelector((state) => state.currentUser)
  const theme = useTheme()
  const { t } = useTranslation()

  let datasets: any = [
    {
      label,
      data: [{ x: value1, y: 0 }],
      backgroundColor: value1 >= 0 ? fsColors.fsDarkBlue.colors[1] : fsColors.fsGray.colors[1],
      pointRadius: 8,
      pointHoverRadius: 8,
    },
  ]
  if (value2 && value3) {
    datasets = [
      ...datasets,
      {
        label: `${t('gender.pay.gap.germany')} (${isMean ? t('mean.value') : t('median.value')})`,
        data: [{ x: value2, y: 0 }],
        backgroundColor: fsColors.fsGray.colors[2],
        pointRadius: 6,
        pointHoverRadius: 6,
      },
      {
        label: `${t('gender.pay.gap.eu')} (${isMean ? t('mean.value') : t('median.value')})`,
        data: [{ x: value3, y: 0 }],
        backgroundColor: theme.palette.background.paper,
        borderColor: fsColors.fsGray.colors[2],
        pointRadius: 6,
        pointHoverRadius: 6,
      },
    ]
  }

  const drawHorizontalLinesPlugin: Plugin<'scatter'> = {
    id: 'horizontalLinesPlugin',
    afterDatasetsDraw: (chart: ChartJS<'scatter'>) => {
      const { ctx, data, scales } = chart
      const xZero = scales.x.getPixelForValue(0)

      ctx.save()
      ctx.strokeStyle = value1 >= 0 ? fsColors.fsDarkBlue.colors[1] : fsColors.fsGray.colors[1]
      ctx.lineWidth = 1

      data.datasets.forEach((dataset) => {
        dataset.data.forEach((point) => {
          if (point && typeof point === 'object' && 'x' in point && 'y' in point) {
            const xPixel = scales.x.getPixelForValue(point.x)
            const yPixel = scales.y.getPixelForValue(point.y)

            ctx.beginPath()
            ctx.moveTo(xZero, yPixel)
            ctx.lineTo(xPixel, yPixel)
            ctx.stroke()
          }
        })
      })

      ctx.restore()
    },
  }

  return (
    <Box pb={2} mt={0} height={height}>
      <Scatter
        className="basic_mean_pay_gap"
        id="basic_mean_pay_gap"
        data={{
          datasets,
        }}
        options={{
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 0,
              right: 50,
              left: 25,
            },
          },
          plugins: {
            legend: {
              display: datasets.length > 1,
              labels: {
                color: theme.palette.text.primary,
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 70,
              },
              onHover: (e: any) => {
                e.native.target.style.cursor = 'pointer'
              },
              onLeave: (e: any) => {
                e.native.target.style.cursor = 'default'
              },
            },
            tooltip: {
              enabled: true,
              mode: 'dataset',
              callbacks: {
                label: (context) => {
                  return `${t('gender.pay.gap.company')} (${
                    isMean ? t('mean.value') : t('median.value')
                    // @ts-ignore
                  }): ${context.raw?.x.toLocaleString(currentLanguage, {
                    maximumFractionDigits: 2,
                  })} %`
                },
              },
            },
            datalabels: {
              anchor: (context) => (context.datasetIndex === 0 ? 'end' : 'start'),
              align: (context) => (context.datasetIndex === 0 ? 'end' : 'start'),
              color: theme.palette.text.primary,
              formatter: (value) =>
                `${Number(value.x).toLocaleString(currentLanguage, {
                  maximumFractionDigits: 2,
                })} %`,
            },
          },
          scales: {
            x: {
              title: {
                display: false,
              },
              type: 'linear',
              offset: true,
              min: roundUpToNearest10(Number(min([value2, value3, value1]) ?? 0) - 10),
              max: roundUpToNearest10(Number(max([value2, value3, value1]) ?? 0) + 10),
              grid: {
                display: true,
                color: theme.palette.divider,
              },
              border: {
                color: theme.palette.text.disabled,
              },
              ticks: {
                autoSkip: true,
                stepSize: 10,
                color: theme.palette.text.secondary,
                callback: (value) =>
                  Number(value) % 5 === 0
                    ? `${Number(value).toLocaleString(currentLanguage, {
                        maximumFractionDigits: 2,
                      })} %`
                    : '',
              },
            },
            y: {
              grid: {
                display: false,
              },
              border: {
                color: theme.palette.text.disabled,
              },
              ticks: {
                color: theme.palette.text.primary,
                padding: 20,
                callback: (value) => (value === 0 ? label : ''),
                stepSize: 1,
                font: {
                  weight: 'bold',
                },
              },
              offset: true,
            },
          },
        }}
        plugins={[drawHorizontalLinesPlugin]}
      />
      {/* {isMean && (
        <Box>
          <Typography variant="caption" ml={2} mb={2}>
            {`\u2731`} {t('gender.source.1')}{' '}
            <Link
              href="https://ec.europa.eu/eurostat/databrowser/view/EARN_GR_GPGR2__custom_610647/bookmark/table?bookmarkId=d1855244-df61-4fdc-914b-5196b3d45af3"
              target="_blank"
            >
              {currentLanguage === 'de'
                ? 'offiziellen Daten von Eurostat'
                : 'official Eurostat data'}
            </Link>{' '}
            {t('gender.source')}.
          </Typography>
        </Box>
      )} */}
    </Box>
  )
}

export default ChartKPIA
